import { useEffect, useState } from 'react';
import { tokenStorage } from '../utils/auth';
import { mediaService } from '../services/mediaService';
import { MediaLoadError, MediaLoadErrorKind } from '../utils/mediaLoadError';

/**
 * Fetches a media file with JWT Authorization header and returns an object URL.
 * Works for any media type (image, video, audio, PDF) — browsers can't send
 * Authorization headers from <video src>, <audio src>, or <iframe src>,
 * so we fetch the blob manually and create an object URL.
 *
 * On failure `errorKind` says why: `missing` (404, the file is not in storage),
 * `unavailable` (503, storage unreachable) or `failed`. There is no automatic retry.
 */
export function useAuthBlobUrl(mediaId: string | null | undefined) {
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<MediaLoadErrorKind | null>(null);

  useEffect(() => {
    if (!mediaId) {
      setUrl('');
      setError(null);
      setErrorKind(null);
      return;
    }

    let revoked = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);
      setErrorKind(null);
      setUrl('');
      try {
        const token = tokenStorage.get();
        const res = await fetch(mediaService.getViewUrl(mediaId), {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new MediaLoadError(res.status);
        }
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) {
          setUrl(objectUrl);
          setLoading(false);
        }
      } catch (e: any) {
        if (!revoked && e.name !== 'AbortError') {
          setError(e.message || 'Failed to load media');
          setErrorKind(e instanceof MediaLoadError ? e.kind : 'failed');
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      revoked = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId]);

  return { url, loading, error, errorKind };
}
